import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Map as PigeonMap, ZoomControl } from 'pigeon-maps';
import { MdSatelliteAlt, MdMap } from 'react-icons/md';
import { FaRegCompass } from 'react-icons/fa';
import { SelectCurrentCoordinates } from '_redux/slices/timeline/timeline-live-location-tab';
import { fetchDeviceEventsAsc } from '_services/coverage-service';
import { resolveWifiEventsToCoords, bleToCoords } from './asset-tracking-utils';
import { selectAirportLocations } from '_redux/slices/timeline/timeline-areas';

const BRAND = '#307FE2';
const OSM_PROVIDER = (x, y, z) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
const SATELLITE_PROVIDER = (x, y, z) =>
    `https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

// helpers 
function toDateInputValue(d) { return d.toISOString().slice(0, 10); }

function fitToPoints(points) {
    if (!points.length) return null;
    const lats = points.map(p => p.lat);
    const lngs = points.map(p => p.lng);
    const center = [(Math.min(...lats) + Math.max(...lats)) / 2, (Math.min(...lngs) + Math.max(...lngs)) / 2];
    const spread = Math.max(Math.max(...lats) - Math.min(...lats), Math.max(...lngs) - Math.min(...lngs));
    const zoom = spread < 0.0005 ? 19 : spread < 0.002 ? 18 : spread < 0.01 ? 16 : spread < 0.05 ? 14 : 12;
    return { center, zoom };
}

// Extract lat/lng from a location_fixed event (root or decoded)
function extractLatLng(evt) {
    const lat = evt.lat ?? evt.decoded?.lat;
    const lng = evt.lng ?? evt.decoded?.lng;
    if (lat != null && lng != null) return { lat, lng };


    return null;
}

function extractBleLatLng(evt, locations) {
    const bleItems = evt.ble ?? evt.decoded?.ble ?? [];
    const c = bleToCoords(bleItems, locations);
    return c ? { lat: c.latitude, lng: c.longitude } : null;
}

// Cluster raw points into a grid (≈11 m cells) and count visits per cell.
function clusterPoints(rawPoints) {
    const PRECISION = 4; // ~11 m
    const map = new Map();
    for (const { lat, lng } of rawPoints) {
        const key = `${lat.toFixed(PRECISION)},${lng.toFixed(PRECISION)}`;
        if (map.has(key)) {
            map.get(key).count++;
        } else {
            map.set(key, { lat: parseFloat(lat.toFixed(PRECISION)), lng: parseFloat(lng.toFixed(PRECISION)), count: 1 });
        }
    }
    return [...map.values()];
}

// canvas heatmap renderer
//
// Classic simpleheat-style technique:
//   1. For each point draw a radial gradient (black → transparent) on a shadow canvas.
//      Overlapping gradients accumulate alpha → intensity image.
//   2. Read pixel data, map each pixel's alpha value through a colour palette
//      (transparent → blue → cyan → green → yellow → orange → red).
//   3. Write the colourised pixels back.
//
// Result: nearby points naturally blend into a single heat signature;
// isolated points stay as cool-coloured halos.

let _palette = null;
function getHeatPalette() {
    if (_palette) return _palette;
    const c = document.createElement('canvas');
    c.width = 256;
    c.height = 1;
    const ctx = c.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 256, 0);
    g.addColorStop(0, 'rgba(0,0,255,0)');
    g.addColorStop(0.15, 'rgba(0,0,255,1)');
    g.addColorStop(0.35, 'rgba(0,220,255,1)');
    g.addColorStop(0.55, 'rgba(0,255,80,1)');
    g.addColorStop(0.72, 'rgba(255,230,0,1)');
    g.addColorStop(0.87, 'rgba(255,100,0,1)');
    g.addColorStop(1, 'rgba(255,0,0,1)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 256, 1);
    const d = ctx.getImageData(0, 0, 256, 1).data;
    _palette = new Uint8ClampedArray(256 * 3);
    for (let i = 0; i < 256; i++) {
        _palette[i * 3] = d[i * 4];
        _palette[i * 3 + 1] = d[i * 4 + 1];
        _palette[i * 3 + 2] = d[i * 4 + 2];
    }
    return _palette;
}

function renderHeatmap(canvas, clusters, latLngToPixel, zoom) {
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    if (!clusters.length) return;

    // Radius in pixels: larger when zoomed in so individual points spread out
    // and merge with neighbours. Clamp so it looks sane at every zoom level.
    const radius = Math.max(12, Math.min(90, Math.pow(2, zoom - 15) * 14));

    const maxCount = Math.max(...clusters.map(c => c.count));

    // Step 1: draw intensity blobs
    clusters.forEach(({ lat, lng, count }) => {
        const [x, y] = latLngToPixel([lat, lng]);

        // Skip points that are completely off-screen (with some margin)
        if (x < -radius || y < -radius || x > w + radius || y > h + radius) return;

        // Weight: heavier blobs for higher visit counts
        const weight = 0.06 + (count / maxCount) * 0.55;
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, radius);
        gradient.addColorStop(0, `rgba(0,0,0,${weight.toFixed(3)})`);
        gradient.addColorStop(0.4, `rgba(0,0,0,${(weight * 0.5).toFixed(3)})`);
        gradient.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
    });

    // Step 2: colourise via palette
    const palette = getHeatPalette();
    const imageData = ctx.getImageData(0, 0, w, h);
    const pix = imageData.data;

    for (let i = 0; i < pix.length; i += 4) {
        const alpha = pix[i + 3];
        if (alpha === 0) continue;
        // Map alpha (0-255) → palette index (0-255). Boost contrast slightly.
        const idx = Math.min(255, Math.floor(alpha * 2.5));
        pix[i]     = palette[idx * 3];
        pix[i + 1] = palette[idx * 3 + 1];
        pix[i + 2] = palette[idx * 3 + 2];
        pix[i + 3] = Math.min(255, Math.floor(alpha * 3.5));
    }

    ctx.putImageData(imageData, 0, 0);
}

// canvas child component 
// pigeon-maps injects latLngToPixel + mapState into direct children with no anchor.

function HeatmapCanvas({ clusters, latLngToPixel, mapState }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!canvasRef.current || !latLngToPixel || !mapState?.width) return;
        const canvas = canvasRef.current;
        canvas.width = mapState.width;
        canvas.height = mapState.height;
        renderHeatmap(canvas, clusters, latLngToPixel, mapState.zoom ?? 17);
    }, [clusters, latLngToPixel, mapState]);

    return (
        <canvas
            ref={canvasRef}
            style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', zIndex: 2 }}
        />
    );
}

// main component 
export default function AssetTrackingMovementHeatmap({ devices }) {
    const airportCoords = useSelector(SelectCurrentCoordinates);
    const locations = useSelector(selectAirportLocations) ?? [];
    const defaultCenter = airportCoords?.lat
        ? [parseFloat(airportCoords.lat), parseFloat(airportCoords.lon)]
        : [53.4264, -6.2499];

    const [search, setSearch] = useState('');
    const [selectedDevice, setSelected] = useState(null);
    const [startDate, setStartDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 7); return toDateInputValue(d); });
    const [endDate, setEndDate] = useState(() => toDateInputValue(new Date()));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [clusters, setClusters] = useState([]);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [mapZoom, setMapZoom] = useState(17);
    const [satellite, setSatellite] = useState(false);

    const filteredDevices = useMemo(() =>
        (devices ?? []).filter(d =>
            !search || d.meta?.deviceName?.toLowerCase().includes(search.toLowerCase())
        ), [devices, search]);

    const provider = useMemo(() => satellite ? SATELLITE_PROVIDER : OSM_PROVIDER, [satellite]);

    async function loadHeatmap() {
        if (!selectedDevice) return;
        setLoading(true);
        setError(null);
        setClusters([]);
        try {
            const tenantId = selectedDevice.meta?.tenantId;
            const appName = selectedDevice.meta?.applicationName;
            const devEui = selectedDevice.id;
            const start = new Date(startDate + 'T00:00:00');
            const end = new Date(endDate + 'T23:59:59');

            const events = await fetchDeviceEventsAsc(tenantId, appName, devEui, start, end);
            const fixes = events.filter(e => e.type === 'location_fixed' || e.type === 'location_fixed_gps_limit');
            const wifiCoords = await resolveWifiEventsToCoords(fixes, selectedDevice.meta?.tenantName);

            const rawPoints = [];
            for (const [idx, evt] of fixes.entries()) {
                const gps = extractLatLng(evt);
                if (gps) { rawPoints.push(gps); continue; }
                const wc = wifiCoords.get(idx);
                if (wc) { rawPoints.push(wc); continue; }
                const ble = extractBleLatLng(evt, locations);
                if (ble) rawPoints.push(ble);
            }

            if (!rawPoints.length) {
                setError('No position fixes found in this date range.');
                setLoading(false);
                return;
            }

            const result = clusterPoints(rawPoints);
            setClusters(result);

            const fit = fitToPoints(rawPoints);
            if (fit) { setMapCenter(fit.center); setMapZoom(fit.zoom); }
        } catch (e) {
            console.error('Heatmap load error:', e);
            setError('Failed to load. Check console for details.');
        } finally {
            setLoading(false);
        }
    }

    const resetMap = useCallback(() => {
        if (clusters.length) {
            const fit = fitToPoints(clusters);
            if (fit) { setMapCenter(fit.center); setMapZoom(fit.zoom); }
        }
    }, [clusters]);

    const totalFixes = useMemo(() => clusters.reduce((s, c) => s + c.count, 0), [clusters]);

    return (
        <div style={{ display: 'flex', height: '100%', gap: 12 }}>

            {/* ── Left panel ── */}
            <div style={{
                width: 250, flexShrink: 0,
                background: 'linear-gradient(175deg, #1e5fbb 0%, #307FE2 100%)',
                borderRadius: 12, overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(48,127,226,0.3)',
                display: 'flex', flexDirection: 'column',
            }}>
                {/* Header */}
                <div style={{ padding: '14px 14px 12px', borderBottom: '1px solid rgba(255,255,255,0.12)', flexShrink: 0 }}>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>Movement Heatmap</div>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 14 }}>

                    {/* Device selector */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                                Select Tracker
                            </div>
                            <span style={{ fontSize: '0.65rem', fontWeight: 800, background: 'rgba(255,255,255,0.18)', color: '#fff', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 20, padding: '1px 7px' }}>
                                {filteredDevices.length}
                            </span>
                        </div>

                        <input
                            type="text"
                            placeholder="Search devices…"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            style={{
                                width: '100%', padding: '6px 10px', boxSizing: 'border-box',
                                background: 'rgba(255, 255, 255, 0.62)', border: '1px solid rgba(255,255,255,0.22)',
                                borderRadius: 7, fontSize: '0.8rem', outline: 'none', color: '#090909',
                            }}
                        />

                        <div style={{ borderRadius: 8, overflowY: 'scroll', height: 190, background: 'rgba(0,0,0,0.12)', padding: 4 }}>
                            {filteredDevices.map(device => {
                                const active = selectedDevice?.id === device.id;
                                return (
                                    <div
                                        key={device.id}
                                        onClick={() => { setSelected(device); setClusters([]); setError(null); }}
                                        style={{
                                            display: 'flex', alignItems: 'stretch',
                                            width: '100%', boxSizing: 'border-box',
                                            background: active ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)',
                                            border: '1px solid rgba(255,255,255,0.12)',
                                            borderRadius: 8, marginBottom: 4, cursor: 'pointer',
                                            overflow: 'hidden', transition: 'background 0.12s', userSelect: 'none',
                                        }}
                                        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
                                        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                                    >
                                        <div style={{ width: 3, background: active ? '#fff' : 'transparent', flexShrink: 0 }} />
                                        <div style={{ padding: '7px 9px', minWidth: 0 }}>
                                            <div style={{ fontSize: '0.8rem', fontWeight: active ? 700 : 500, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {device.meta?.deviceName || device.id}
                                            </div>
                                            {device.meta?.deviceName && (
                                                <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {device.id}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {filteredDevices.length === 0 && (
                                <div style={{ padding: '16px 10px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                                    No devices found
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }} />

                    {/* Date range */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                            Date Range
                        </div>
                        <div>
                            <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 3 }}>From</label>
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                                style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.8rem', padding: '5px 8px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.12)', color: '#fff', colorScheme: 'dark' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.6)', display: 'block', marginBottom: 3 }}>To</label>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
                                style={{ width: '100%', boxSizing: 'border-box', fontSize: '0.8rem', padding: '5px 8px', borderRadius: 7, border: '1px solid rgba(255,255,255,0.22)', background: 'rgba(255,255,255,0.12)', color: '#fff', colorScheme: 'dark' }}
                            />
                        </div>
                    </div>

                    <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)' }} />

                    {/* Load button */}
                    <button
                        onClick={loadHeatmap}
                        disabled={!selectedDevice || loading}
                        style={{
                            padding: '8px 0', borderRadius: 7, border: 'none', fontWeight: 700, fontSize: '0.83rem',
                            background: (!selectedDevice || loading) ? 'rgba(255,255,255,0.12)' : '#fff',
                            color: (!selectedDevice || loading) ? 'rgba(255,255,255,0.35)' : BRAND,
                            cursor: (!selectedDevice || loading) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.15s',
                        }}
                    >
                        {loading ? 'Loading…' : 'Load Heatmap'}
                    </button>

                    {/* Stats */}
                    {clusters.length > 0 && (
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div style={{ fontSize: '0.63rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)' }}>
                                Summary
                            </div>
                            {[
                                { label: 'Total fixes', value: totalFixes },
                                { label: 'Unique locations', value: clusters.length },
                                { label: 'Hottest spot', value: `${Math.max(...clusters.map(c => c.count))} visits` },
                            ].map(({ label, value }) => (
                                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)' }}>
                                    <span>{label}</span><span style={{ fontWeight: 600 }}>{value}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {error && (
                        <div style={{ fontSize: '0.75rem', color: '#fef3c7', background: 'rgba(0,0,0,0.2)', borderRadius: 6, padding: '7px 10px', lineHeight: 1.4, border: '1px solid rgba(255,211,77,0.3)' }}>
                            {error}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Map panel ── */}
            <div style={{
                flex: 1, position: 'relative',
                borderRadius: 10, overflow: 'hidden',
                border: '1px solid #dbeafe',
            }}>
                {/* Empty state overlay */}
                {!selectedDevice && (
                    <div style={{
                        position: 'absolute', inset: 0, zIndex: 10,
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(240,246,255,0.92)', borderRadius: 10, pointerEvents: 'none', gap: 10,
                    }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%', background: '#E6F0FB',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke={BRAND} strokeWidth="1.8">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e3a5f' }}>Select a tracker</div>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>Choose a device and load its heatmap</div>
                    </div>
                )}

                {/* Loading overlay */}
                {loading && (
                    <div style={{
                        position: 'absolute', inset: 0, zIndex: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: 'rgba(240,246,255,0.85)', borderRadius: 10,
                    }}>
                        <div style={{ color: BRAND, fontWeight: 600, fontSize: '0.88rem' }}>Building heatmap…</div>
                    </div>
                )}

                <PigeonMap
                    center={mapCenter}
                    zoom={mapZoom}
                    provider={provider}
                    onBoundsChanged={({ center, zoom }) => { setMapCenter(center); setMapZoom(zoom); }}
                    style={{ height: '100%', width: '100%' }}
                >
                    {/* Canvas heatmap — pigeon-maps injects latLngToPixel + mapState */}
                    {clusters.length > 0 && <HeatmapCanvas clusters={clusters} />}
                    <ZoomControl />
                </PigeonMap>

                {/* Map controls */}
                <div className="map-overlay-controls">
                    <button className="map-overlay-btn" onClick={resetMap} title="Fit to heatmap">
                        <FaRegCompass size={16} />
                    </button>
                    <button
                        className="map-overlay-btn"
                        onClick={() => setSatellite(v => !v)}
                        title={satellite ? 'Street view' : 'Satellite'}
                    >
                        {satellite ? <MdMap size={16} /> : <MdSatelliteAlt size={16} />}
                    </button>
                </div>

                {/* Colour legend */}
                <div style={{
                    position: 'absolute', bottom: 24, left: 14, zIndex: 5,
                    background: 'rgba(255,255,255,0.92)', borderRadius: 8,
                    padding: '7px 10px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                    display: 'flex', flexDirection: 'column', gap: 4,
                }}>
                    <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#64748b', marginBottom: 2 }}>Frequency</div>
                    <div style={{
                        width: 120, height: 10, borderRadius: 5,
                        background: 'linear-gradient(to right, #0000ff, #00dcff, #00ff50, #ffe600, #ff6400, #ff0000)',
                    }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.6rem', color: '#94a3b8' }}>
                        <span>Low</span><span>High</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
